import { ApiError } from "../api/client";
import { LiffLoginIncompleteError } from "../lib/liff";

export const LOGIN_RECOVERY_FALLBACK_MESSAGE =
  "ログインに失敗しました。ホーム画面に戻り、もう一度お試しください。";

type FlowFailureView = {
  userMessage: string;
  allowRetry: boolean;
};

function hasApiErrorCode(error: unknown, code: string): boolean {
  return error instanceof ApiError && error.code === code;
}

function isSessionCookieEstablishmentError(error: unknown): boolean {
  return error instanceof Error && error.message === "Session cookie could not be established";
}

function isTenantMismatchError(error: unknown): boolean {
  return hasApiErrorCode(error, "TENANT_SESSION_MISMATCH") || hasApiErrorCode(error, "TENANT_NOT_FOUND");
}

function isLineLoginVerificationError(error: unknown): boolean {
  return hasApiErrorCode(error, "INVALID_LINE_TOKEN");
}

function isLineIdTokenAvailabilityError(error: unknown): boolean {
  return (
    error instanceof Error
    && (error.message === "LINE ID token is not available"
      || error.message === "LINE ID token does not match the configured LIFF channel")
  );
}

export function isRetryableLoginRecoveryFailure(error: unknown): boolean {
  return (
    error instanceof LiffLoginIncompleteError
    || isLineLoginVerificationError(error)
    || isLineIdTokenAvailabilityError(error)
    || isSessionCookieEstablishmentError(error)
  );
}

export function classifyEntryFailure(error: unknown, hasOwnerOnboardingToken: boolean): FlowFailureView {
  if (hasApiErrorCode(error, "OWNER_ONBOARDING_INVITE_NOT_FOUND")) {
    return {
      userMessage:
        "オーナー初期設定トークンが見つかりません。招待URL全体を開き直すか、新しい招待を利用してください。",
      allowRetry: false
    };
  }

  if (hasApiErrorCode(error, "OWNER_ONBOARDING_INVITE_EXPIRED")) {
    return {
      userMessage: "オーナー初期設定トークンの有効期限が切れています。新しい招待を発行してください。",
      allowRetry: false
    };
  }

  if (hasApiErrorCode(error, "OWNER_ONBOARDING_INVITE_REVOKED")) {
    return {
      userMessage: "このオーナー初期設定トークンは無効化されています。新しい招待を利用してください。",
      allowRetry: false
    };
  }

  if (hasApiErrorCode(error, "OWNER_ONBOARDING_INVITE_ALREADY_USED")) {
    return {
      userMessage:
        "このオーナー初期設定トークンは別のアカウントで使用済みです。利用するLINEアカウントを確認してください。",
      allowRetry: false
    };
  }

  if (isTenantMismatchError(error)) {
    return {
      userMessage: "現在のtenantではこの招待を利用できません。招待URLの tenant を確認してください。",
      allowRetry: false
    };
  }

  if (isRetryableLoginRecoveryFailure(error)) {
    return {
      userMessage: LOGIN_RECOVERY_FALLBACK_MESSAGE,
      allowRetry: true
    };
  }

  return {
    userMessage: hasOwnerOnboardingToken ? "管理者権限の設定に失敗しました。" : LOGIN_RECOVERY_FALLBACK_MESSAGE,
    allowRetry: !hasOwnerOnboardingToken
  };
}

export function classifyMemberInviteFailure(error: unknown): FlowFailureView {
  if (hasApiErrorCode(error, "MEMBER_INVITE_NOT_FOUND")) {
    return {
      userMessage: "member invite token が見つかりません。招待URL全体を開き直すか、新しい招待を利用してください。",
      allowRetry: false
    };
  }

  if (hasApiErrorCode(error, "MEMBER_INVITE_EXPIRED")) {
    return {
      userMessage: "member invite token の有効期限が切れています。管理者に新しい招待を発行してもらってください。",
      allowRetry: false
    };
  }

  if (hasApiErrorCode(error, "MEMBER_INVITE_REVOKED")) {
    return {
      userMessage: "この member invite は無効化されています。管理者に新しい招待を発行してもらってください。",
      allowRetry: false
    };
  }

  if (hasApiErrorCode(error, "MEMBER_INVITE_ALREADY_USED")) {
    return {
      userMessage: "この member invite は別のアカウントで使用済みです。利用するLINEアカウントを確認してください。",
      allowRetry: false
    };
  }

  if (isTenantMismatchError(error)) {
    return {
      userMessage: "現在のtenantではこの member invite を利用できません。招待URLの tenant を確認してください。",
      allowRetry: false
    };
  }

  if (isRetryableLoginRecoveryFailure(error)) {
    return {
      userMessage: LOGIN_RECOVERY_FALLBACK_MESSAGE,
      allowRetry: true
    };
  }

  return {
    userMessage: "member invite の処理に失敗しました。",
    allowRetry: false
  };
}

export function classifyStudentInviteFailure(error: unknown): FlowFailureView {
  if (hasApiErrorCode(error, "STUDENT_SELF_INVITE_NOT_FOUND")) {
    return {
      userMessage:
        "生徒本人用招待 token が見つかりません。招待URL全体を開き直すか、新しい招待を利用してください。",
      allowRetry: false
    };
  }

  if (hasApiErrorCode(error, "STUDENT_SELF_INVITE_EXPIRED")) {
    return {
      userMessage:
        "生徒本人用招待 token の有効期限が切れています。管理者に新しい招待を発行してもらってください。",
      allowRetry: false
    };
  }

  if (hasApiErrorCode(error, "STUDENT_SELF_INVITE_REVOKED")) {
    return {
      userMessage: "この生徒本人用招待は無効化されています。管理者に新しい招待を発行してもらってください。",
      allowRetry: false
    };
  }

  if (hasApiErrorCode(error, "STUDENT_SELF_INVITE_ALREADY_USED")) {
    return {
      userMessage:
        "この生徒本人用招待は別のアカウントで使用済みです。利用するLINEアカウントを確認してください。",
      allowRetry: false
    };
  }

  if (isTenantMismatchError(error)) {
    return {
      userMessage: "現在のtenantではこの生徒本人用招待を利用できません。招待URLの tenant を確認してください。",
      allowRetry: false
    };
  }

  if (isRetryableLoginRecoveryFailure(error)) {
    return {
      userMessage: LOGIN_RECOVERY_FALLBACK_MESSAGE,
      allowRetry: true
    };
  }

  return {
    userMessage: "生徒本人用招待の処理に失敗しました。",
    allowRetry: false
  };
}
